import React, { useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import type { MediaAssetResponse } from 'we-tree-api-client/v2';
import { mediaApi } from '@/api/mediaWrapper';
import HelpDot from '@/components/help/HelpDot';
import { VIDEO_UPLOAD_TIP } from '@/data/help-inline-tips';
import { useDomainStore } from '@/hooks/useStores';
import type { MediaUploadAcceptKinds } from '@/types/media-upload';
import { getFamilyPlanUpgradeHint, isFamilyOwner } from '@/utils/familyPlan';
import {
  getFileAcceptAttribute,
  getUploadAriaLabel,
  getUploadPromptHint,
  validateMediaUploadFile
} from '@/utils/mediaUpload';

import './media-upload-form.css';

export interface MediaUploadFormProps {
  familyId: string;
  /**
   * When set, uploads via profile/gallery APIs for that member.
   * When omitted, uploads as family-level media (library / citation path).
   */
  familyMemberId?: string;
  /** `images` (default) or `all` (image + video + audio). Profile photo UIs should keep `images`. */
  acceptKinds?: MediaUploadAcceptKinds;
  /** Called with the confirmed asset after a successful upload. */
  onSuccess?: (asset: MediaAssetResponse) => void;
  /** Compact mode for inline use (e.g. within ProfilePhotoUpload) */
  compact?: boolean;
  /** When true (default), sets uploaded image as primary profile photo. Ignored without familyMemberId. */
  setAsPrimaryProfilePhoto?: boolean;
  /**
   * Soft client gate for video/audio. When false, paid-only types are rejected before upload.
   * API remains authoritative when omitted.
   */
  hasPaidPlan?: boolean;
}

const MediaUploadForm: React.FC<MediaUploadFormProps> = observer(
  ({
    familyId,
    familyMemberId,
    acceptKinds = 'images',
    onSuccess,
    compact = false,
    setAsPrimaryProfilePhoto = true,
    hasPaidPlan
  }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const domainStore = useDomainStore();
    const upgradeHint = getFamilyPlanUpgradeHint(
      domainStore.selectedFamilyId === familyId && isFamilyOwner(domainStore.currentFamily?.userRole)
    );
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [upgradeRequired, setUpgradeRequired] = useState(false);
    const [softWarning, setSoftWarning] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const handleFiles = useCallback(
      async (files: FileList | null) => {
        const file = files?.[0];
        if (!file || !familyId) return;

        const validation = validateMediaUploadFile(file, { acceptKinds, hasPaidPlan });
        if (!validation.ok) {
          setUploadError(validation.message);
          setUpgradeRequired(Boolean(validation.upgradeRequired));
          setSoftWarning(null);
          return;
        }

        setUploadError(null);
        setUpgradeRequired(false);
        setSoftWarning(validation.softWarning ?? null);
        setUploadSuccess(false);
        setIsUploading(true);
        setUploadProgress(0);

        const result = familyMemberId
          ? await mediaApi.uploadProfilePictureWithProgress(
              familyId,
              familyMemberId,
              file,
              (percent) => setUploadProgress(percent),
              setAsPrimaryProfilePhoto
            )
          : await mediaApi.uploadFamilyMedia(familyId, file, (percent) => setUploadProgress(percent));

        setIsUploading(false);
        setUploadProgress(100);

        if (result.success) {
          setUploadSuccess(true);
          setSoftWarning(null);
          onSuccess?.(result.data);
          setTimeout(() => {
            setUploadSuccess(false);
            setUploadProgress(0);
          }, 3000);
        } else {
          const err = result.error;
          setUploadError(err?.getUserMessage() ?? t('upload.failed'));
          setUpgradeRequired(Boolean(err?.isUpgradeRequired()));
        }
      },
      [familyId, familyMemberId, acceptKinds, hasPaidPlan, onSuccess, setAsPrimaryProfilePhoto, t]
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
      },
      [handleFiles]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    }, []);

    const handleFileChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
        e.target.value = '';
      },
      [handleFiles]
    );

    const handleClick = useCallback(() => {
      if (!isUploading) fileInputRef.current?.click();
    }, [isUploading]);

    const handleRetry = useCallback(() => {
      setUploadError(null);
      setUpgradeRequired(false);
      setSoftWarning(null);
      fileInputRef.current?.click();
    }, []);

    const block = 'media-upload-form';
    const modifier = compact ? `${block}--compact` : '';
    const accept = getFileAcceptAttribute(acceptKinds);
    const ariaLabel = getUploadAriaLabel(acceptKinds);
    const promptHint = getUploadPromptHint(acceptKinds);
    const showVideoTip = acceptKinds === 'all';

    return (
      <div className={`${block} ${modifier}`.trim()}>
        <div
          className={`${block}__dropzone ${isDragging ? `${block}__dropzone--active` : ''} ${isUploading ? `${block}__dropzone--uploading` : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          aria-label={ariaLabel}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className={`${block}__file-input`}
            aria-label={acceptKinds === 'all' ? t('upload.chooseMedia') : t('upload.chooseImage')}
            disabled={isUploading}
          />
          {isUploading ? (
            <div className={`${block}__progress`}>
              <div className={`${block}__progress-bar`} style={{ width: `${uploadProgress}%` }} />
              <span className={`${block}__progress-text`}>
                {uploadProgress < 100
                  ? t('upload.uploading', { percent: Math.round(uploadProgress) })
                  : t('upload.processing')}
              </span>
            </div>
          ) : (
            <div className={`${block}__prompt`}>
              <span className={`${block}__prompt-icon`} aria-hidden>
                {acceptKinds === 'all' ? '📁' : '📷'}
              </span>
              <span className={`${block}__prompt-text`}>
                {compact
                  ? t('upload.promptCompact')
                  : acceptKinds === 'all'
                    ? t('upload.promptMedia')
                    : t('upload.promptImage')}
              </span>
              <span className={`${block}__prompt-hint-row`}>
                <span className={`${block}__prompt-hint`}>{promptHint}</span>
                {showVideoTip && (
                  <HelpDot
                    title={VIDEO_UPLOAD_TIP.title}
                    body={VIDEO_UPLOAD_TIP.body}
                    storageKey={VIDEO_UPLOAD_TIP.storageKey}
                  />
                )}
              </span>
            </div>
          )}
        </div>

        {softWarning && !uploadError && (
          <p className={`${block}__soft-warning`} role="status">
            {softWarning}
          </p>
        )}

        {uploadError && (
          <div className={`${block}__error`} role="alert">
            <div className={`${block}__error-body`}>
              <p className={`${block}__error-message`}>{uploadError}</p>
              {upgradeRequired && <p className={`${block}__upgrade-hint`}>{upgradeHint}</p>}
            </div>
            {!upgradeRequired && (
              <button type="button" className={`${block}__retry-button`} onClick={handleRetry}>
                {tCommon('retry')}
              </button>
            )}
          </div>
        )}

        {uploadSuccess && (
          <p className={`${block}__success`} role="status">
            {t('upload.complete')}
          </p>
        )}
      </div>
    );
  }
);

export default MediaUploadForm;
